import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import {
  MarketingNav,
  MarketingFooter,
} from "@/components/TailorWizMarketingHome";

/**
 * Marcus's full TailorWiz vs ChatGPT comparison, written long-form. Source
 * text comes from his TailorWiz_vs_ChatGPT.docx — preserved as-is, broken
 * into sections matching the doc structure (Honest Question / 4 Differences
 * / Side-by-side / Comparison chart / Bottom line).
 */
export default function TailorWizVsChatGPT() {
  // Native title + description setter — keeps things simple without pulling
  // in react-helmet-async (which isn't installed in this app).
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "TailorWiz vs. ChatGPT — The Honest Comparison";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") || "";
    const desc =
      "ChatGPT reads the job description. TailorWiz reads the company. The honest comparison nobody else will give you — and why surface keyword tailoring is the reason you are still being ignored in 2026.";
    if (meta) meta.setAttribute("content", desc);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = desc;
      document.head.appendChild(m);
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <MarketingNav />

      {/* HERO ------------------------------------------------------------ */}
      <section className="bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] text-white">
        <div className="max-w-4xl mx-auto px-6 py-20 sm:py-28 text-center">
          <p className="text-[#C9A84C] tracking-[0.3em] text-[12px] font-bold">
            TAILORWIZ
          </p>
          <h1 className="mt-3 text-4xl sm:text-6xl font-extrabold leading-tight">
            TailorWiz <span className="text-[#C9A84C]">vs.</span> ChatGPT
          </h1>
          <p className="mt-4 text-[18px] sm:text-[20px] text-slate-200 max-w-2xl mx-auto">
            The honest comparison nobody else will give you.
          </p>
          <p className="mt-2 text-[15px] text-slate-300 max-w-2xl mx-auto">
            Why surface-level keyword tailoring is the reason you are still
            being ignored in 2026.
          </p>
          <p className="mt-8 text-[12px] tracking-widest text-slate-400">
            BY MARCUS HALL · RESULTS DRIVEN RESUMES / EXECUTIVE JOBS ON DEMAND
          </p>
        </div>
      </section>

      {/* THE HONEST QUESTION --------------------------------------------- */}
      <Section eyebrow="THE HONEST QUESTION">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0B1C3F]">
          If ChatGPT Is Free, Why Would Anyone Pay for TailorWiz?
        </h2>
        <Body>
          It is the question almost every prospective TailorWiz user asks before
          they sign up. ChatGPT is free. It is available right now. It can
          rewrite a resume in seconds. So why would anyone pay for TailorWiz?
        </Body>
        <Body>
          The honest answer takes a few minutes to walk through. Once you see it
          laid out, you will understand why ChatGPT, Claude, Teal, Rezi, and
          every other AI resume tool on the market are producing the same
          resume, why that resume is now being filtered out by the very
          algorithm you thought it was beating, and why TailorWiz operates on a
          completely different level.
        </Body>
        <Pullquote>
          ChatGPT reads the job description. TailorWiz reads the company.
        </Pullquote>
      </Section>

      {/* DIFFERENCE 01 --------------------------------------------------- */}
      <Section
        eyebrow="DIFFERENCE 01"
        title="The Solution Asset (ChatGPT Cannot Build This)"
        bg="muted"
      >
        <Body>
          Before we get to how TailorWiz tailors a resume, you need to
          understand the document that puts TailorWiz on a different planet
          entirely. It is called the Solution Asset, and ChatGPT cannot build
          it. Not because it does not have the writing ability. Because it does
          not have the intelligence behind it.
        </Body>

        <H3>What the Solution Asset Does</H3>
        <Body>
          Instead of submitting a generic cover letter that essentially says
          “please consider me for this role,” the Solution Asset is a one-page
          executive positioning document that does something completely
          different. It opens by naming the company’s real, verified pain
          point. The actual problem the department is under pressure to solve
          right now. Then it proves you have already solved that exact problem
          before, using accomplishments and results from your real background.
          It closes with a peer-to-peer invitation to a twenty-minute
          conversation.
        </Body>
        <Body>
          It is not a letter asking for a job. It is a consultant’s brief that
          positions you as the answer to a problem the hiring manager has been
          losing sleep over.
        </Body>

        <H3>Why ChatGPT Cannot Produce This</H3>
        <Body>
          Ask ChatGPT to write a cover letter and it will give you something
          polished. Ask it to write a Solution Asset and it cannot, for one
          simple reason: ChatGPT has no idea what the company’s real pain
          points actually are. It will guess. It will fabricate. It will
          produce something that sounds plausible but is not grounded in
          verified evidence. And the moment a hiring manager reads a claim that
          does not match their reality, you are dead in the water.
        </Body>
        <Body>
          TailorWiz does not guess. Through JobIntel 360 it pulls verified,
          multi-source intelligence on the company before a single word of the
          Solution Asset is written. The earnings call language. The Glassdoor
          patterns. The LinkedIn posts from the hiring manager. The news
          coverage. The press releases. The SEC filings. All of it. Then it
          cross-references that intelligence against your real background to
          find the matches where you have already solved exactly what they are
          trying to solve.
        </Body>

        <H3>The Outcome Difference</H3>
        <Body>
          Solution Asset users see an{" "}
          <strong className="text-[#0B1C3F]">85% interview success rate</strong>{" "}
          compared to the 2 to 5% average that traditional applicants are
          working with. Most candidates go from application to interview within
          72 hours instead of waiting 4 to 6 weeks.
        </Body>
        <Body>
          ChatGPT cannot replicate that because the Solution Asset is not a
          writing problem. It is an intelligence problem. And ChatGPT does not
          have the intelligence.
        </Body>
      </Section>

      {/* DIFFERENCE 02 --------------------------------------------------- */}
      <Section
        eyebrow="DIFFERENCE 02"
        title="Decision Maker Discovery and Warmup (ChatGPT Cannot Do This)"
      >
        <Body>
          This is the difference most people do not even know to ask about. And
          it is the difference that compresses a four to six month executive
          search into a thirty day campaign.
        </Body>

        <H3>The Application Black Hole Reframe</H3>
        <Body>
          80% or more of qualified executive applicants get killed by ATS and
          AI filters before anyone with hiring power ever sees them. The 2%
          response rate exists not because candidates are not good enough but
          because the system is designed to filter them out before anyone with
          hiring power ever encounters them.
        </Body>
        <Body>
          Submitting through the careers page is the slowest, lowest-yield path
          to an interview that exists. The hiring manager who would have hired
          you on the spot never sees your resume. ChatGPT does not solve this.
          ChatGPT just helps you write a slightly better version of the resume
          that gets filtered out.
        </Body>

        <H3>What TailorWiz Does Instead</H3>
        <Body>
          TailorWiz finishes the job that every other resume tool quits on.
          After the documents are built, it does three things ChatGPT cannot:
        </Body>
        <ul className="space-y-3 my-4 pl-6 list-disc text-slate-700 text-[16px] leading-relaxed">
          <li>
            <strong className="text-[#0B1C3F]">FINDS THE DECISION MAKER.</strong>{" "}
            TailorWiz identifies the actual hiring manager for the role, not
            the recruiter, not the HR coordinator, not the application portal.
            The person with the power to call you in.
          </li>
          <li>
            <strong className="text-[#0B1C3F]">SOURCES THEIR DIRECT CONTACT.</strong>{" "}
            Email, LinkedIn, and any other verified contact channels.
          </li>
          <li>
            <strong className="text-[#0B1C3F]">BUILDS YOUR WARMUP.</strong>{" "}
            Before you send anything cold, TailorWiz builds you a complete
            warmup sequence. The decision maker’s recent LinkedIn posts you can
            engage with thoughtfully. Mutual connections you can leverage for a
            warm intro. Recent company news you can reference. Industry
            conversations the hiring manager is active in. Everything you need
            to stop being a stranger and start being a familiar name by the
            time your Solution Asset hits their inbox.
          </li>
        </ul>

        <H3>Why ChatGPT Cannot Do This</H3>
        <Body>
          ChatGPT does not have real-time access to LinkedIn. It cannot search
          a company’s leadership structure. It does not know who posted what
          last week. It cannot identify mutual connections. It cannot tell you
          which of the hiring manager’s recent posts is worth engaging with or
          why. It can give you generic networking advice. It cannot do the
          actual research.
        </Body>
        <Body>
          TailorWiz can. Because it was built for this specific job, not for
          generating poems and birthday messages on the side.
        </Body>
        <Pullquote>
          The hiring manager who would have hired you on the spot never sees
          your resume. Until now.
        </Pullquote>
      </Section>

      {/* DIFFERENCE 03 --------------------------------------------------- */}
      <Section
        eyebrow="DIFFERENCE 03"
        title="Surface Tailoring vs Deep Research Tailoring"
        bg="muted"
      >
        <Body>
          This is the most important difference of all, and it is the reason
          every other AI resume tool on the market is making your job search
          harder in 2026 instead of easier.
        </Body>

        <H3>How ChatGPT, Teal, Rezi, and Every Other AI Tool Tailor a Resume</H3>
        <Body>
          All of them do the same thing. You paste the job description. The
          tool extracts the keywords, phrases, and stated requirements. It
          rewrites your resume to mirror those keywords. It produces something
          that addresses the stated requirements of the posting in slightly
          different language.
        </Body>
        <Body>
          That is surface tailoring. It addresses what the company put on the
          page. Nothing more.
        </Body>
        <Body>
          Here is the problem. Every other candidate applying for the same role
          is doing the exact same thing. Two hundred candidates run the same
          job description through the same tools and produce two hundred
          resumes that all address the same stated requirements. The output
          clusters together statistically. Modern ATS systems trained on
          machine learning now recognize that cluster and surface the outliers,
          the resumes that do not look like the AI-generated pile, for human
          review.
        </Body>
        <Body>
          The candidate who used generic AI to look like everyone else is now
          being filtered out by the very system they thought they were gaming.
        </Body>

        <H3>How TailorWiz Tailors a Resume</H3>
        <Body>
          TailorWiz tailors on two levels simultaneously, and that is what no
          other AI tool on the market does.
        </Body>
        <Body>
          <strong className="text-[#0B1C3F]">LEVEL ONE</strong> is the job
          description. TailorWiz reads it, extracts every requirement, every
          responsibility, every keyword, and maps your real experience to it.
          Same as every other tool. This is the floor.
        </Body>
        <Body>
          <strong className="text-[#0B1C3F]">LEVEL TWO</strong> is where
          TailorWiz separates itself entirely. Before it writes a single word
          of your resume, it deploys JobIntel 360 to do deep research on the
          company across thousands of verified sources. Earnings calls. SEC
          filings. Glassdoor reviews. Blind threads. LinkedIn posts from the
          hiring manager and their leadership team. News archives. Press
          releases. Industry publications. Podcast transcripts. Court records.
          Employee review platforms. Salary databases. Social media channels.
          Video content. Financial filings. Regulatory databases. More than
          6,000 verified source categories in total.
        </Body>
        <Body>
          What it is looking for is the company’s real pain points. The
          strategic priority the CEO mentioned on the last earnings call that
          has not made its way into a single internal communication. The
          turnover issue draining productivity in the department. The cultural
          shift the new VP has been driving for the last eight months. The
          customer satisfaction crisis the team is under pressure to fix. The
          competitive threat that just landed in their market.
        </Body>
        <Body>
          None of this is in the job description. None of it will ever be in
          the job description. The company carefully crafts postings to look
          attractive to candidates, which means they will never tell you the
          truth about what is actually happening on the inside.
        </Body>
        <Body>
          TailorWiz finds it anyway. And then it tailors your resume both to
          the job description AND to the real pain points. Now your resume is
          not just addressing the stated requirements like every other
          AI-tailored resume in the pile. It is speaking directly to the
          verified reality of what this company actually needs from the person
          they hire.
        </Body>

        <H3>The 2026 ATS Algorithm and Why This Matters</H3>
        <Body>
          The ATS landscape in 2026 is not what it was three years ago. The
          rules that used to work, keyword density, simple formatting,
          predictable section headers, are no longer sufficient. ChatGPT and
          the other AI tools are still optimizing for the 2018 ATS. TailorWiz
          was built with a deep, current understanding of how the 2026 systems
          actually evaluate resumes. Semantic relevance. Modern parsing.
          Section weighting. Cluster recognition. Outlier detection. Everything
          ChatGPT does not know about.
        </Body>
        <Body>
          That is how TailorWiz hits a 100% ATS pass rate while ChatGPT-tailored
          resumes get filtered out as part of the cluster.
        </Body>

        <H3>The Emotional Trigger Difference</H3>
        <Body>
          Passing the ATS is only half the job. The other half is what happens
          when the hiring manager reads the resume.
        </Body>
        <Body>
          A ChatGPT-tailored resume makes the hiring manager think,{" "}
          <em>this person can probably do the job. Qualified. Move to the
          maybe pile.</em>
        </Body>
        <Body>
          A TailorWiz Advanced Tailored resume makes the hiring manager think,{" "}
          <em>
            this person has already solved the exact problem I am dealing with
            right now. I need to talk to them before someone else does.
          </em>{" "}
          That is an emotional trigger. That is the moment a hiring manager
          picks up the phone instead of saving you for later. That is the
          difference between an interview in 72 hours and an application that
          ages in a database.
        </Body>
        <Body>
          ChatGPT cannot hit that trigger because it does not know what the
          trigger is.
        </Body>
        <Pullquote>
          Surface keywords win at the ATS. Real pain points win at the human.
        </Pullquote>
      </Section>

      {/* DIFFERENCE 04 — SIDE BY SIDE ----------------------------------- */}
      <Section
        eyebrow="DIFFERENCE 04"
        title="What Actually Happens When You Hit Submit"
      >
        <Body>Here is the side by side. Same candidate. Same job. Two different tools.</Body>

        <div className="grid md:grid-cols-2 gap-5 mt-8">
          {/* ChatGPT side */}
          <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-6">
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="w-5 h-5 text-slate-500" />
              <h3 className="text-[16px] font-extrabold tracking-wide text-slate-700">
                CHATGPT
              </h3>
            </div>
            <ol className="space-y-2 text-[14px] text-slate-700 leading-relaxed list-decimal pl-5">
              <li>You paste the job description.</li>
              <li>It extracts keywords and mirrors them in your resume.</li>
              <li>
                Your resume now looks like the 200 other resumes also tailored
                with ChatGPT.
              </li>
              <li>Modern ATS recognizes the cluster and filters you out.</li>
              <li>
                If you survive the ATS, the hiring manager reads a resume that
                says <em>I am qualified for this role.</em>
              </li>
              <li>Move to the maybe pile. Ages in the database.</li>
              <li>You have no idea who the decision maker is.</li>
              <li>You apply, you wait, you refresh your inbox.</li>
            </ol>
            <div className="mt-5 grid grid-cols-2 gap-3 text-center">
              <div className="rounded-lg bg-white border border-slate-200 p-3">
                <div className="text-[11px] tracking-wider text-slate-500 font-bold">
                  RESPONSE
                </div>
                <div className="text-[18px] font-extrabold text-slate-700">
                  2–5%
                </div>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3">
                <div className="text-[11px] tracking-wider text-slate-500 font-bold">
                  TIME TO INTERVIEW
                </div>
                <div className="text-[18px] font-extrabold text-slate-700">
                  4–6 weeks
                </div>
              </div>
            </div>
          </div>

          {/* TailorWiz side */}
          <div className="rounded-2xl border-2 border-[#C9A84C] bg-gradient-to-br from-[#0B1C3F] to-[#0E2350] text-white p-6 shadow-xl shadow-[#0B1C3F]/30">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-[#C9A84C]" />
              <h3 className="text-[16px] font-extrabold tracking-wide text-[#C9A84C]">
                TAILORWIZ
              </h3>
            </div>
            <ol className="space-y-2 text-[14px] text-slate-100 leading-relaxed list-decimal pl-5">
              <li>You paste the job description.</li>
              <li>
                TailorWiz confirms you are qualified with the Qualifications
                Match Tool.
              </li>
              <li>
                It deploys JobIntel 360 to research the company across 6,000+
                verified sources.
              </li>
              <li>It identifies the company’s real, verified pain points.</li>
              <li>
                It tailors your resume both to the job description AND to the
                pain points.
              </li>
              <li>
                It builds your Solution Asset, cover letter, outreach letter,
                and LinkedIn summary in the same run.
              </li>
              <li>
                It finds the actual hiring manager and sources their direct
                contact.
              </li>
              <li>
                It builds your warmup sequence with the hiring manager’s posts
                and mutual connections.
              </li>
              <li>
                100% ATS pass rate. Documents land in the decision maker’s
                inbox directly.
              </li>
            </ol>
            <div className="mt-5 grid grid-cols-2 gap-3 text-center">
              <div className="rounded-lg bg-white/10 border border-[#C9A84C]/40 p-3">
                <div className="text-[11px] tracking-wider text-[#C9A84C] font-bold">
                  RESPONSE
                </div>
                <div className="text-[18px] font-extrabold text-white">
                  35–45%
                </div>
              </div>
              <div className="rounded-lg bg-white/10 border border-[#C9A84C]/40 p-3">
                <div className="text-[11px] tracking-wider text-[#C9A84C] font-bold">
                  TIME TO INTERVIEW
                </div>
                <div className="text-[18px] font-extrabold text-white">
                  72 hours
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* THE COMPLETE COMPARISON CHART ---------------------------------- */}
      <Section
        eyebrow="THE COMPLETE COMPARISON"
        title="TailorWiz vs ChatGPT: Side by Side"
        bg="muted"
      >
        <Body>Every difference that matters, in one chart.</Body>
        <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="bg-[#0B1C3F] text-white">
                <th className="text-left px-5 py-4 font-bold tracking-wide">
                  CAPABILITY
                </th>
                <th className="text-left px-5 py-4 font-bold tracking-wide text-slate-300">
                  CHATGPT
                </th>
                <th className="text-left px-5 py-4 font-bold tracking-wide text-[#C9A84C]">
                  TAILORWIZ
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {COMPARISON_ROWS.map(([cap, gpt, tw], i) => (
                <tr key={cap} className={i % 2 === 0 ? "bg-slate-50/40" : "bg-white"}>
                  <td className="px-5 py-3 font-semibold text-[#0B1C3F] align-top">
                    {cap}
                  </td>
                  <td className="px-5 py-3 text-slate-700 align-top">{gpt}</td>
                  <td className="px-5 py-3 text-slate-800 align-top">
                    <span className="font-medium">{tw}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* BOTTOM LINE ---------------------------------------------------- */}
      <Section
        eyebrow="THE BOTTOM LINE"
        title="ChatGPT Is a General Purpose Tool. TailorWiz Is a Specialist."
      >
        <Body>
          ChatGPT was trained on the internet. It can write a poem, draft an
          email, summarize a book, debug code, and yes, rewrite a resume. It is
          a general purpose model that does many things at a moderate level.
        </Body>
        <Body>
          TailorWiz was built for one job. To get qualified job seekers,
          leaders, and professionals out of the application black hole and into
          the decision maker’s inbox in under 72 hours. Every tool inside it
          was designed around that mission. Every algorithm was tuned for it.
          Every output was trained on the methodology of a world renowned
          executive resume writer who has spent 14+ years doing this work.
        </Body>
        <Pullquote>ChatGPT can write you a resume. TailorWiz can win you the role.</Pullquote>
        <Body>
          Five minutes inside TailorWiz Advanced Tailoring is the highest-ROI
          hour of your entire job search. For job seekers at every level
          range, compressing your timeline by even one month is worth tens of
          thousands of dollars in earned income, plus the months of stress
          your family will never have to feel.
        </Body>

        <div className="mt-10 text-center">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#0B1C3F] text-white font-bold text-[15px] shadow-lg border-2 border-[#C9A84C] hover:bg-[#0E2350] transition-colors"
          >
            Try It Out FREE Now <ArrowRight className="w-4 h-4 text-[#C9A84C]" />
          </Link>
          <p className="mt-3 text-[12px] text-slate-500">
            No credit card needed. Cancel anytime.
          </p>
        </div>
      </Section>

      <MarketingFooter />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Small inline layout helpers — keep the JSX above readable.
// ────────────────────────────────────────────────────────────────────────
function Section({
  eyebrow,
  title,
  bg = "white",
  children,
}: {
  eyebrow?: string;
  title?: string;
  bg?: "white" | "muted";
  children: React.ReactNode;
}) {
  return (
    <section className={bg === "muted" ? "bg-slate-50" : "bg-white"}>
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
        {eyebrow && (
          <p className="text-[#C9A84C] tracking-[0.3em] text-[12px] font-bold">
            {eyebrow}
          </p>
        )}
        {title && (
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0B1C3F]">
            {title}
          </h2>
        )}
        <div className={title || eyebrow ? "mt-6 space-y-5" : "space-y-5"}>
          {children}
        </div>
      </div>
    </section>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[16px] sm:text-[17px] leading-relaxed text-slate-700">
      {children}
    </p>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[19px] sm:text-[21px] font-bold text-[#0B1C3F] mt-8">
      {children}
    </h3>
  );
}

function Pullquote({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="my-8 border-l-4 border-[#C9A84C] pl-5 text-[18px] sm:text-[20px] font-semibold italic text-[#0B1C3F] leading-snug">
      {children}
    </blockquote>
  );
}

const COMPARISON_ROWS: Array<[string, string, string]> = [
  ["Tailoring depth", "Surface keywords from JD", "JD + real company pain points"],
  ["Company intelligence", "None", "6,000+ verified sources"],
  ["Built by an executive resume writer", "No", "Yes (Marcus Hall, 14+ years)"],
  ["2026 ATS algorithm understanding", "No", "Yes, built for it"],
  ["ATS pass rate", "Variable, filtered as cluster", "100% pass rate"],
  ["Match score on tailored documents", "Unverifiable", "90%+ guaranteed every run"],
  ["Solution Asset capability", "No", "Yes, 85% interview rate"],
  ["Cover letter generation", "Yes, generic", "Yes, intelligence-backed"],
  ["LinkedIn profile rebuild", "No, advice only", "Yes, full rebuild"],
  ["Executive bio generation", "Generic", "Trained on Marcus’s voice"],
  ["Decision maker discovery", "No", "Yes, finds actual hiring manager"],
  ["Hiring manager contact sourcing", "No", "Yes, direct contact"],
  ["Warmup sequence building", "No", "Yes, posts + mutual connections"],
  ["Bypass the ATS and recruiters", "No", "Yes, direct delivery"],
  ["Saved jobs library", "No", "Yes"],
  ["Document version history", "No", "Yes"],
  ["Before and after match report", "No", "Yes"],
  ["Industry-specific language library", "Generic", "Native vocabulary, dozens of industries"],
  ["Keyword intelligence panel", "No", "Yes"],
  ["Multi-document tailoring in one run", "No", "Yes, full package"],
  ["Honest tailoring (no fabrication)", "Frequently fabricates", "Flags anything unverified"],
  ["Response rate (executive roles)", "2 to 5%", "35 to 45%"],
  ["Time from application to interview", "4 to 6 weeks", "72 hours"],
  ["Built specifically for job seekers", "No, general purpose", "Yes, every level"],
];
